/*!

=========================================================
* Argon Dashboard React - v1.2.2
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import {
  Badge,
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import Header from "components/Headers/Header";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import { GAME_DETAIL_API, REGION_API } from "../../services/CONSTANTS";
import { getApiDataService } from "../../services/apiServices";
import GameInfoEdit from "views/components/game/GameInfoEdit";
import UnitPriceEdit from "views/components/game/UnitPriceEdit";

const EditGame = () => {
  const { id } = useParams();
  const [game, setGame] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [regionsLoading, setRegionsLoading] = useState(true);
  const [regions, setRegions] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState(null);

  useEffect(() => {
    fetchGameDetail();
    fetchRegions();
  }, []);

  useEffect(() => {
    if (regions.length === 0) {
      setSelectedRegionId(null);
      return;
    }
    const stillValid = regions.some((r) => r.id === selectedRegionId);
    if (!stillValid) {
      setSelectedRegionId(regions[0].id);
    }
  }, [regions, selectedRegionId]);

  const onSubmitComplete = () => {
    fetchGameDetail();
  };

  const fetchGameDetail = () => {
    setIsLoading(true);
    getApiDataService(GAME_DETAIL_API(id))
      .then((res) => {
        setGame(res.data);
        setIsLoading(false);
      })
      .catch((err) => {
        console.log("Error in useEffect!", err);
        setIsLoading(false);
        setGame({});
      });
  };

  const fetchRegions = () => {
    setRegionsLoading(true);
    getApiDataService(REGION_API + "?status=active")
      .then((res) => {
        setRegions(res.data || []);
        setRegionsLoading(false);
      })
      .catch((err) => {
        console.log("Error in useEffect!", err);
        setRegions([]);
        setRegionsLoading(false);
      });
  };

  const tabStatusHandler = (e, regionId) => {
    e.preventDefault();
    setSelectedRegionId(regionId);
  };

  const selectedRegion =
    regions.find((r) => r.id === selectedRegionId) || null;
  const pageLoading = isLoading || regionsLoading;

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <GameInfoEdit
          game={game}
          regions={regions}
          id={id}
          onSubmitComplete={onSubmitComplete}
        />

        <Container className="mt-7" fluid>
          <Row className="justify-content-center">
            <Col className="order-xl-1" xl="12">
              <Card className="shadow bg-secondary">
                <CardHeader className="bg-white border-0">
                  <Row className="align-items-center">
                    <Col xs="10">
                      <h3 className="mb-0">ဂိမ်းယူနစ်ဈေးနှုန်းများပြင်ရန်</h3>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  {pageLoading ? (
                    <div className="akh-game-edit-skeleton">
                      <div className="akh-skeleton mb-3" style={{ height: "2.75rem", width: "100%" }} />
                      <div className="akh-skeleton-card mb-3" />
                      <div className="akh-skeleton-card mb-3" />
                      <div className="akh-skeleton-card" />
                    </div>
                  ) : (
                    <>
                      <div className="nav-wrapper mb-3">
                        <Nav
                          className="akh-region-tabs"
                          id="tabs-icons-text"
                          pills
                          role="tablist"
                        >
                          {regions.map((region) => (
                            <NavItem key={region.id}>
                              <NavLink
                                aria-selected={selectedRegionId === region.id}
                                className={
                                  selectedRegionId === region.id ? "active" : ""
                                }
                                onClick={(e) => tabStatusHandler(e, region.id)}
                                href="#pablo"
                                role="tab"
                              >
                                <span className="akh-region-tabs__label">
                                  {region.country_name}
                                </span>
                                {region.currency_code && (
                                  <Badge
                                    color="secondary"
                                    pill
                                    className="akh-region-tabs__badge font-mono ml-2"
                                  >
                                    {region.currency_code}
                                  </Badge>
                                )}
                              </NavLink>
                            </NavItem>
                          ))}
                        </Nav>
                      </div>

                      {selectedRegion && game?.id ? (
                        <UnitPriceEdit
                          game={game}
                          region={selectedRegion}
                          key={selectedRegion.id}
                        />
                      ) : (
                        <div className="akh-empty-state">
                          <i className="fas fa-globe" aria-hidden="true" />
                          <span>No active regions available</span>
                        </div>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </Container>
    </>
  );
};

export default EditGame;
